import { Controller } from "@hotwired/stimulus"

// "+ New Task / Event" inline form. Full entry — title, type,
// department, due date, status, assign to (with groups), description.
// Explicit Save and Cancel buttons; nothing autosaves while the form
// is open. Esc cancels.
export default class extends Controller {
  static targets = ["rowTemplate", "mountPoint"]
  static values = { createUrl: String, recurrenceUrl: String }

  // Two page-open behaviors driven by the URL, both one-shot (the params
  // are stripped so a reload after saving does not replay them):
  //   ?open=<task id>    opens that task's side panel (Duplicate lands here)
  //   ?new_for=<user id> opens the New Task form with that intern ticked
  //                      (the intern profile page's Assign Task button)
  connect() {
    const params = new URLSearchParams(window.location.search)
    const openId = params.get("open")
    const newFor = params.get("new_for")
    if (!openId && !newFor) return
    params.delete("open")
    params.delete("new_for")
    const rest = params.toString()
    window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""))
    // Next tick: the row and side-panel controllers connect after this one.
    setTimeout(() => {
      if (openId) document.querySelector(`.task-row[data-task-id='${openId}']`)?.click()
      if (newFor) {
        this.startNew()
        const box = this.assignmentPickerEl?.querySelector(`input[type="checkbox"][value="${newFor}"]`)
        if (box) {
          box.checked = true
          box.dispatchEvent(new Event("change", { bubbles: true }))
        }
      }
    }, 50)
  }

  startNew(event) {
    event?.preventDefault?.()
    if (!this.hasRowTemplateTarget || !this.hasMountPointTarget) return
    if (this.activeRow) return

    const fragment = this.rowTemplateTarget.content.cloneNode(true)
    this.mountPointTarget.appendChild(fragment)
    this.activeRow = this.mountPointTarget.lastElementChild

    // Cloned-template targets don't bind to THIS controller (templates
    // are inert). Resolve elements manually.
    this.titleInputEl = this.activeRow.querySelector("[data-intern-task-create-target='titleInput']")
    this.departmentSelectEl = this.activeRow.querySelector("[data-intern-task-create-target='departmentSelect']")
    this.entryTypeSelectEl = this.activeRow.querySelector("[data-intern-task-create-target='entryTypeSelect']")
    this.createdBySelectEl = this.activeRow.querySelector("[data-intern-task-create-target='createdBySelect']")
    this.dueDateInputEl = this.activeRow.querySelector("[data-intern-task-create-target='dueDateInput']")
    this.statusSelectEl = this.activeRow.querySelector("[data-intern-task-create-target='statusSelect']")
    this.statusContainerEl = this.activeRow.querySelector("[data-intern-task-create-target='statusContainer']")
    this.priorityCheckboxEl = this.activeRow.querySelector("[data-intern-task-create-target='priorityCheckbox']")
    this.assignmentPickerEl = this.activeRow.querySelector("[data-controller~='assignment-picker']")
    this.descriptionTextareaEl = this.activeRow.querySelector("[data-intern-task-create-target='descriptionTextarea']")
    this.repeatsSelectEl = this.activeRow.querySelector("[data-intern-task-create-target='repeatsSelect']")
    this.weekdaysBoxEl = this.activeRow.querySelector("[data-intern-task-create-target='weekdaysBox']")
    this.monthDayBoxEl = this.activeRow.querySelector("[data-intern-task-create-target='monthDayBox']")
    this.monthDaySelectEl = this.activeRow.querySelector("[data-intern-task-create-target='monthDaySelect']")
    this.dueDateContainerEl = this.activeRow.querySelector("[data-intern-task-create-target='dueDateContainer']")
    this.repeatsHintEl = this.activeRow.querySelector("[data-intern-task-create-target='repeatsHint']")

    this.titleInputEl?.focus()
  }

  // "Repeats" (Amanda 2026-09-17): weekly shows the day boxes, monthly the
  // day-of-month picker, and either hides Due Date, because each copy is
  // due the day it appears.
  onRepeatsChange() {
    const mode = this.repeatsSelectEl?.value || "none"
    if (this.weekdaysBoxEl) this.weekdaysBoxEl.hidden = mode !== "weekly"
    if (this.monthDayBoxEl) this.monthDayBoxEl.hidden = mode !== "monthly"
    if (this.dueDateContainerEl) this.dueDateContainerEl.style.display = mode === "none" ? "" : "none"
    if (this.repeatsHintEl) this.repeatsHintEl.hidden = mode === "none"
  }

  readRepeats() {
    const mode = this.repeatsSelectEl?.value || "none"
    if (mode === "weekly") {
      const days = Array.from(this.weekdaysBoxEl?.querySelectorAll('input[type="checkbox"]:checked') || []).map((b) => b.value)
      return { cadence: "weekly", weekdays: days }
    }
    if (mode === "monthly") return { cadence: "monthly", month_day: this.monthDaySelectEl?.value || "1" }
    return null
  }

  async submit() {
    const title = this.titleInputEl?.value?.trim()
    if (!title) {
      alert("Title is required.")
      this.titleInputEl?.focus()
      return
    }
    if (this.submitting) return
    this.submitting = true

    const department = this.departmentSelectEl?.value || "brand_management"
    const entryType = this.entryTypeSelectEl?.value || "task"
    const dueDate = this.dueDateInputEl?.value || null
    const description = this.descriptionTextareaEl?.value || ""

    // Events have no status workflow; force pending on the server side too.
    const status = entryType === "event" ? "pending" : (this.statusSelectEl?.value || "pending")

    // Read assignment from the picker. Build the payload based on the
    // picker's kind: none / group / single user / multi-user.
    let assigned_to_id = null
    let assigned_to_group = null
    let assigned_user_ids = null
    const pickerSelection = this.readAssignmentPicker()
    switch (pickerSelection.kind) {
      case "group": assigned_to_group = pickerSelection.value; break
      case "user":  assigned_to_id = pickerSelection.value; break
      case "users": assigned_user_ids = pickerSelection.value; break
    }

    const createdById = this.createdBySelectEl?.value || null
    const priority = this.priorityCheckboxEl?.checked ? "high" : "medium"

    // A repeating task is a TEMPLATE, posted to the recurrences endpoint;
    // the server makes today's copy when today is a due day and sends us
    // to the Recurring tab, where the template is listed.
    const repeats = this.readRepeats()
    if (repeats) {
      if (repeats.cadence === "weekly" && repeats.weekdays.length === 0) {
        alert("Pick at least one day for it to repeat on.")
        this.submitting = false
        return
      }
      const recurrencePayload = {
        intern_task_recurrence: {
          title: title,
          description: description,
          department: department,
          priority: priority,
          entry_type: entryType,
          created_by_id: createdById,
          assigned_to_id: assigned_to_id,
          assigned_to_group: assigned_to_group,
          cadence: repeats.cadence,
          weekdays: repeats.weekdays || [],
          month_day: repeats.month_day || null
        }
      }
      if (assigned_user_ids) recurrencePayload.assigned_user_ids = assigned_user_ids
      try {
        const response = await fetch(this.recurrenceUrlValue, {
          method: "POST",
          headers: this.headers(),
          body: JSON.stringify(recurrencePayload)
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          alert(`Could not save: ${(data.errors || []).join(", ") || response.status}`)
          this.submitting = false
          return
        }
        window.location.assign(data.redirect || window.location.pathname)
      } catch (err) {
        console.error("intern_task_create (recurring) failed:", err)
        alert("Could not save. Try again.")
        this.submitting = false
      }
      return
    }

    const payload = {
      shared_task: {
        title: title,
        description: description,
        department: department,
        priority: priority,
        status: status,
        entry_type: entryType,
        due_date: dueDate,
        assigned_to_id: assigned_to_id,
        assigned_to_group: assigned_to_group,
        created_by_id: createdById
      }
    }
    if (assigned_user_ids) payload.assigned_user_ids = assigned_user_ids

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify(payload)
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        alert(`Could not create: ${(data.errors || []).join(", ") || response.status}`)
        this.submitting = false
        return
      }
      window.location.reload()
    } catch (err) {
      console.error("intern_task_create failed:", err)
      alert("Could not create. Try again.")
      this.submitting = false
    }
  }

  // Esc cancels. Enter is no longer a submit shortcut here — the form
  // has too many fields to assume Enter means "save now".
  onKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    }
  }

  // Events have no status workflow; hide the Status field when the
  // user picks Event so the form matches the row + panel behavior.
  onTypeChange() {
    if (!this.statusContainerEl) return
    const isEvent = this.entryTypeSelectEl?.value === "event"
    this.statusContainerEl.style.display = isEvent ? "none" : ""
  }

  // Reads the assignment-picker controller's currentValue without
  // requiring a direct Stimulus reference. Walks DOM nodes for the
  // selected radio/checkboxes — same logic as the controller's
  // currentValue() method.
  readAssignmentPicker() {
    if (!this.assignmentPickerEl) return { kind: "none" }
    const groupRadio = this.assignmentPickerEl.querySelector('input[type="radio"]:checked')
    if (groupRadio && groupRadio.value) return { kind: "group", value: groupRadio.value }
    const checked = Array.from(this.assignmentPickerEl.querySelectorAll('input[type="checkbox"]:checked'))
    const ids = checked.map((cb) => parseInt(cb.value, 10))
    if (ids.length === 0) return { kind: "none" }
    if (ids.length === 1) return { kind: "user", value: ids[0] }
    return { kind: "users", value: ids }
  }

  cancel() {
    if (this.activeRow) {
      this.activeRow.remove()
      this.activeRow = null
      this.titleInputEl = null
      this.departmentSelectEl = null
      this.entryTypeSelectEl = null
      this.createdBySelectEl = null
      this.dueDateInputEl = null
      this.statusSelectEl = null
      this.statusContainerEl = null
      this.priorityCheckboxEl = null
      this.assignmentPickerEl = null
      this.descriptionTextareaEl = null
      this.repeatsSelectEl = null
      this.weekdaysBoxEl = null
      this.monthDayBoxEl = null
      this.monthDaySelectEl = null
      this.dueDateContainerEl = null
      this.repeatsHintEl = null
      this.submitting = false
    }
  }

  headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      "Accept": "application/json"
    }
  }
}
